
	import { Component, Vue, Watch, Prop } from "vue-property-decorator";
	import { namespace, Getter } from "vuex-class";
	import Multiselect from "vue-multiselect";
	import { DispatchGuard } from "@/store/dispatch/types";

	import { EventShareUser, EventDetails } from "@/store/site-monitor/types";
	import { ActionState } from "@/types/ActionState";
	import CloseEventDTO from "@/types/CloseEventDTO";
	import CloseMultipleEventsDTO  from "@/types/CloseMultipleEventsDTO";
	import CloseEventResultDTO from "@/types/CloseEventResultDTO";
	import { EventOutcomeResponse, EventOutcome } from "@/types/EventOutcomeDTO";
	import wait from "@/utils/wait";
	import api from "@/services/api.service";

	const SiteMonitor = namespace("siteMonitor");
	const Dispatch = namespace("dispatch");
	const EventCategorySetup = namespace("eventCategorySetup");
	const Eventqueue = namespace("eventqueue");
	const Tasks = namespace("tasks");


	@Component({
		components: {
			multiselect: Multiselect,
		},
	})
	export default class EventClose extends Vue {
		$refs!: {
			eventCloseModal: any;
		};

		@SiteMonitor.Getter("eventOutcomesFlatList") outcomesFlatList: EventOutcome[];

		@SiteMonitor.Getter getEventShare!: any[];
		@SiteMonitor.Getter("getEventCloseShown") eventCloseShown!: boolean;
		@SiteMonitor.Getter getIsUploadingFile: boolean;
		@SiteMonitor.Getter("getEventRecordsAwaitingAckCount") awaitingAck: number;
		@Getter("getPreviousRoute") private returnPath: string;
		@Getter private getFeature: (featureNames: string[]) => boolean;

		@SiteMonitor.Getter("getEventShare") private eventShares: EventShareUser[];
		@SiteMonitor.Getter("getIsUploadingFile") private isUploadingFile: boolean;
		@SiteMonitor.Getter private getEventDetails: EventDetails;

		@SiteMonitor.Mutation private setEventCloseShown: (visible: boolean) => void;
		@SiteMonitor.Mutation private setEventDetails: (eventDetails: EventDetails) => void;

		@SiteMonitor.Action private loadEventOutcomesForAnyAlarm: () => Promise<EventOutcome[]>;
		@SiteMonitor.Action private loadOutcomesForEvent: (eventId: number) => Promise<EventOutcomeResponse>;
		@SiteMonitor.Action private fetchEventDetails: (eventId: number) => Promise<EventDetails>;
		@SiteMonitor.Action eventRecordAllAcknowledgement: any;
		@SiteMonitor.Action private forceCloseMultipleEvents: (
			data: CloseMultipleEventsDTO
		) => Promise<CloseEventResultDTO>;
		@SiteMonitor.Action private closeEvent: (data: CloseEventDTO) => Promise<CloseEventResultDTO>;

		@Dispatch.Action private setDispatchedGuards: (guards: DispatchGuard[]) => void;
		@Dispatch.Getter("getDispatchedGuards") private dispatchedGuards: DispatchGuard[];

		@EventCategorySetup.Action fetchEventCategoriesList: () => Promise<void>;

		@Eventqueue.Getter("getSelectedAlarms") selectedAlarms: number[];
		@Eventqueue.Mutation removeSelectedAlarm: (data: number) => void;
		@Eventqueue.Mutation setRefreshQueue: (refresh:boolean) => void;

		@Tasks.Getter hasEventOutcomeTask: boolean;

		@Prop(Number) eventid?: number;
		@Prop({default: true}) singleEvent: boolean;
		@Prop({default: false, type: Boolean}) eventOnTest: boolean;

		public eventRecords: any[] = [];
		public errors: any = {
			http: {
				getOutcomes: null,
				closeEvent: null,
			},
			form: {
				isValid: true,
				outcomeChoice: {
					message: null,
					isValid: null,
				},
				outcomeNote: {
					message: null,
					isValid: null,
				},
			},
		};

		public closeRequest = {};
		public lastRecord: number = 0;

		// tree of all possible outcomes, of unpredictable depth, as received by API
		public outcomeTree: any[] = [];
		// to allow for infinite depth in outcomes tree above, this array maintains currrent
		// breadcrumbs path of selections made by the user, and is used as a support datastructre
		public outcomeSelectionPath: any[] = [];
		// is iterated by template to display
		public outcomeSelectionControls: any[] = [];
		public latestChoice?: EventOutcome;
		public clearGuards: boolean = false;
		public outcomeNote: string = "";
		public eventCloseError: string | null = null;

		public confirmState: ActionState = ActionState.Undefined;

		readonly singleEventConfirmText: string = "OK";
		readonly multipleEventConfirmText: string = "Confirm";
		// Stores the actual value for the confirm button
		public confirmBtnText = "";

		readonly singleEventModalTitle: string = "Close";
		readonly multipleEventModalTitle: string = "End Events";
		// Stores the actual value for the confirm button
		public modalTitle = "";

		public get otherUserCount() {
			const SharedUsers = this.eventShares.filter((user: any) => user.isActive).length;
			return SharedUsers - 1;
		}

		public get currentSelectedOutcome() {
			const lastOutcome = this.outcomeSelectionPath[this.outcomeSelectionPath.length - 1];
			if (lastOutcome == null) return "";

			return lastOutcome.title;
		}

		public created() {
			this.setEventCloseShown(false);
		}

		public resetData() {
			this.eventCloseError = null;
			this.outcomeNote = "";
			this.confirmState = ActionState.Undefined;
			this.modalTitle = "";
			this.confirmBtnText = "";
		}

		// Force validation when the outcome note (UI) changes
		@Watch("outcomeNote")
		public onReasonChange() {
			this.validate();
		}

		@Watch("eventCloseShown")
		public async onEventCloseShownChanged(value: boolean, oldValue: boolean) {
			if (value) {
				if (!this.eventOnTest) {
					await this.loadOutcomes();
				}

				this.resetData();

				if (this.singleEvent) {
					this.modalTitle = this.singleEventModalTitle;
					this.confirmBtnText = this.singleEventConfirmText;

					if (this.useEventOutcomeTask) {
						this.latestChoice = null;
						await this.fetchEventDetails(this.eventid);
						await this.fetchEventCategoriesList();

						this.setOutcomePath(this.getEventDetails.eventOutcomeID)
					}

				}
				else {
					this.modalTitle = this.multipleEventModalTitle;
					this.confirmBtnText = this.multipleEventConfirmText;
				}

				this.$refs.eventCloseModal.show();

				// Wait for the next tick - the modal should be visible then
				await this.$nextTick();

				const outcomeSelect0 = (this.$refs["outcomeSelect0"] as any)[0];

				// Only set focus if the event outcome wasn't set by the event outcome task
				if (!this.useEventOutcomeTask) {
					setTimeout(() => {
						outcomeSelect0.$el.focus();
					}, 0.5 * this.$config.ANIMATION_DURATION);
				}

			} else {
				this.$refs.eventCloseModal.hide();
			}
		}

		public async loadOutcomes() {
			let eventOutcomeTree: EventOutcome[];
			if (this.singleEvent) {
				const response = await this.loadOutcomesForEvent(this.eventid)
				eventOutcomeTree = response.eventOutcomeTree;
			}
			else {
				eventOutcomeTree = await this.loadEventOutcomesForAnyAlarm();
			}

			this.outcomeTree = eventOutcomeTree;
			this.outcomeSelectionControls = [
				{
					title: "root",
					eventOutcomeID: 0,
					childOutcomes: [...this.outcomeTree],
				},
			];
			this.outcomeSelectionPath = [""]; // placeholder for binding dropdown model to
		}

		public async onConfirmClicked() {
			let closureSuccess: boolean = false;

			this.validate();

			if (this.errors.form.isValid) {
				this.confirmState = ActionState.InProcess;
				// Either closing a single or multiple event
				if (this.singleEvent) {
					closureSuccess = await this.closeSingleEvent(this.eventid,
														   this.latestChoice.eventOutcomeID,
														   this.outcomeNote);
				} else {
					closureSuccess = await this.closeMultipleEvents();
				}

				// if this is a bulk close
				// set the flag in the state to trigger the event queue refresh
				if (!this.singleEvent) {
					this.setRefreshQueue(true);
				}

				// Depending on whether the closure worked either close the dialog
				// or keep open to show the error
				if (closureSuccess) {
					this.confirmState = ActionState.Success;

					await wait(1500);

					this.setEventCloseShown(false);
					// if this was opened from a new tab, close the window
					if (this.singleEvent) {
						if (window.opener !== null){
							window.close();
						}
						else {
							// send the user back to where they came from (if not set, return to event queue)
							let url = this.returnPath == "/" ? "/event-queue" : this.returnPath;
							await this.$router.push({ path: url });
						}
					} else {
						await this.$router.push("/event-queue");
					}
				}
				else {
						this.confirmState = ActionState.Fail;
						await wait(1500);
						this.confirmState = ActionState.Undefined;
				}
			}
		}

		public async closeMultipleEvents(): Promise<boolean> {
			const payload: CloseMultipleEventsDTO = {
					eventIds: this.selectedAlarms,
					eventOutcomeId: this.latestChoice.eventOutcomeID,
					outcomeNote: this.outcomeNote,
					clearGuards: true
				};

			const result: CloseEventResultDTO = await this.forceCloseMultipleEvents(payload);
			if (!result.success) {
				this.eventCloseError = result.error;
			}

			return result.success
		}

		public async closeSingleEvent(id: number, outcomeId: number, note: string): Promise<boolean> {

			if (this.eventOnTest) {
				const result = await api.endTest(id);

				return result.status == 200;
			}

			// TODO refactor into action
			const payload: CloseEventDTO = {
					eventId: id,
					eventOutcomeId: outcomeId,
					outcomeNote: note,
					clearGuards: true
				};

			const result: CloseEventResultDTO = await this.closeEvent(payload);

			if (result.success) {
				this.setDispatchedGuards(null);
				this.setEventDetails(null);

				// The event could have been selected as part of multiple close
				// before being entered into and close individually
				// Check if the id is in the array of select Alarms and if so remove it
				if (this.selectedAlarms.includes(id)) {
					this.removeSelectedAlarm(id);
				}
			}
			else {
				this.eventCloseError = result.error;
			}

			return result.success;
		}

		public cancelCloseEvent() {
			this.setEventCloseShown(false);
		}

		// called whenever ANY select box changes value
		public eventOutcomeSelectionChange(latestChoice: EventOutcome, focus: boolean = true) {
			this.latestChoice = latestChoice;

			const changeParentID = latestChoice.parentID;

			if (changeParentID != null) {
				let controls = this.outcomeSelectionControls;
				let path = this.outcomeSelectionPath;

				let pathIndex = path.findIndex((p) => p.eventOutcomeID === changeParentID);
				let controlIndex = controls.findIndex((p) => p.eventOutcomeID === changeParentID);

				path.splice(pathIndex + 2, path.length);
				controls.splice(controlIndex + 1, controls.length);
			} else {
				this.outcomeSelectionPath = [];
				this.outcomeSelectionControls.splice(1, this.outcomeSelectionControls.length);
			}

			let newChoiceControl = this.addNextChoiceControl(latestChoice);

			// if changed value is not on the end of breadcrumb trail:
			// remove list tail after changed value;

			this.validate();

			if (newChoiceControl) {
				setTimeout(() => {
					let index = this.outcomeSelectionControls.length - 1;
					let outcomeSelect = (this.$refs["outcomeSelect" + index.toString()] as any)[0];
					if (focus) {
						outcomeSelect.$el.focus();
					}
				}, 0.5 * this.$config.ANIMATION_DURATION);
			}
		}

		/**
		 * Allows setting the event outcome path to a specified event outcome, regardless of it's position in the tree.
		 * Traverses the tree upwards until it finds the root outcome, then pushes each outcome in onto the path from root down.
		 * @param eventOutcomeId The id of the event outcome to set the outcome path to be
		 */
		public setOutcomePath(eventOutcomeId: number) {
			const eventOutcome = this.outcomesFlatList.find(outcome => outcome.eventOutcomeID == eventOutcomeId);

			if (!eventOutcome) {
				return
			}

			if (eventOutcome.parentID) {
				this.setOutcomePath(eventOutcome.parentID);
			}
			this.eventOutcomeSelectionChange(eventOutcome, false);
			this.outcomeSelectionPath.push(eventOutcome);
		}

		// @todo - ensure that in future we use a validation library (vuelidate), makes doing this stuff
		// a thousand times simpler!!
		private validate(): boolean {
			this.errors.form.isValid = true;

			// Audit/Test events do not require an event outcome
			if (this.eventOnTest) {
				return true;
			}

			// selected outcome is leaf node of possible outcomes tree
			if (!EventClose.isLeafNode(this.latestChoice)) {
				this.errors.form["outcomeChoice"].message = "Please select a specific outcome";
				this.errors.form["outcomeChoice"].isValid = false;
				this.errors.form.isValid = false;
			} else {
				this.errors.form["outcomeChoice"].message = null;
				this.errors.form["outcomeChoice"].isValid = null;
			}

			// If this is bulk close then the outcome note is mandatory
			if (!this.singleEvent) {
				if (this.outcomeNote.length < 1) {
					this.errors.form["outcomeNote"].message = "Note is mandatory when closing mutliple events";
					this.errors.form["outcomeNote"].isValid = false;
					this.errors.form.isValid = false;
				}
			}

			if (this.errors.form.isValid)
			{
				// outcome note less than 5000 symbols, optional
				if (this.outcomeNote.length > 5000) {
					this.errors.form["outcomeNote"].message = "Note too long - max length of 5000 chars allowed";
					this.errors.form["outcomeNote"].isValid = false;
					this.errors.form.isValid = false;
				} else {
					this.errors.form["outcomeNote"].message = null;
					this.errors.form["outcomeNote"].isValid = null;
				}
			}

			return this.errors.form.isValid;
		}

		/**
		 * Checks if an event outcome is a leaf node
		 * An event outcome is a leaf node if it has no children, but is a valid outcome
		 * @param outcome The event outcome to check
		 * @private
		 */
		private static isLeafNode(outcome: EventOutcome): boolean {
			return outcome?.childOutcomes?.length === 0;
		}

		private getParent(nodeOutcome: any, findOutcome: any) {
			if (nodeOutcome.childOutcomes != null) {
				let foundOutcome = null;
				nodeOutcome.childOutcomes.forEach((outcome: any) => {
					if (outcome.eventOutcomeID == findOutcome.eventOutcomeID) {
						foundOutcome = nodeOutcome;
					}
				});

				if (foundOutcome != null) {
					return foundOutcome;
				}

				nodeOutcome.childOutcomes.forEach((outcome: any) => {
					let recurseOutcome = this.getParent(outcome, findOutcome);
					if (recurseOutcome != null) {
						foundOutcome = recurseOutcome;
					}
				});

				if (foundOutcome != null) {
					return foundOutcome;
				}
			}

			return null;
		}

		private addNextChoiceControl(currentChoice: any) {
			if (currentChoice && currentChoice.childOutcomes != null && currentChoice.childOutcomes.length) {
				this.outcomeSelectionControls.push(currentChoice);
				return true;
			} else {
				return false;
			}
		}

		// Gets the enum so it can be referenced in the template
        public get actionState (): typeof ActionState {
            return ActionState;
        }

		private get useEventOutcomeTask(): boolean {
			return this.getFeature(["Actions", "EventOutcome"]) && this.singleEvent && this.hasEventOutcomeTask;
		}
	}
